import { EventEmitter, Output } from '@angular/core';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Code } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { sortBy } from 'lodash';
import { Guid } from 'guid-typescript';

@Component({
  standalone: false,
  selector: 'app-formly-designer-service-type-edit',
  templateUrl: './formly-designer-service-type-edit.component.html',
  styleUrls: ['./formly-designer-service-type-edit.component.scss']
})
export class FormlyDesignerServiceTypeEditComponent implements OnInit
{
  @Input() public fields: FormlyFieldConfig[];
  @Input() public isDeleted = false;
  @Input() public isLocked = false;

  @Output() public formChanged: EventEmitter<any> = new EventEmitter();

  public editForm: any;
  public serviceTypes: UntypedFormArray;
  public serviceTypesLookup: Code[];
  public loaded = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dictionaryService: DictionaryService) { }

  @Input() public set model(serviceTypes: any)
  {
    this.initModel(serviceTypes);
  }

  public ngOnInit(): void
  {
    this.loaded = false;
    this.dictionaryService.getCachedCommunityServiceTypes().subscribe({
      next: (data) =>
      {
        this.serviceTypesLookup = sortBy(data, ['displayName']);
      }
    });
  }

  /**
   * Add a service type entry
   */
  public addServiceType(id: number = null, version = 0, serviceType: any = null, score: number = null,
    query: any = null, showRules = true, importError = false): void
  {
    this.editForm.controls.serviceTypes.push(this.initServiceType(id, version, serviceType, score, query, showRules, importError));
    if (id === null)
    {
      this.editForm.markAsDirty();
    }
  }

  /**
   * Remove a service type entry
   *
   * @param i Index
   */
  public removeServiceType(i: number): void
  {
    const control = this.editForm.controls.serviceTypes;
    control.removeAt(i);
    this.editForm.markAsDirty();
  }

  public showRulesChanged(e: any): void
  {
    this.editForm.controls.serviceTypes.controls[e.index].controls.showRules.value = e.show;
    // If the rule is opened, we assumed a state changes.
    if (!this.isDeleted && !this.isLocked)
    {
      this.editForm.markAsDirty();
      this.formChanged.emit(this.editForm);
    }
  }

  /**
   * Used for the trackBy to prevent rendering the DOM and losing field focus.
   *
   * @param index Row index.
   * @param item Row item.
   */
  public serviceTypesTrackBy(index: number, item: any): string
  {
    return item.value.guid;
  }

  /**
   * Disable controls when Assessement is deleted
   */
  private disableControls(): void
  {
    if (this.isDeleted || this.isLocked)
    {
      this.editForm.controls.serviceTypes.controls.forEach(data =>
      {
        data.controls.serviceType.disable();
        data.controls.score.disable();
      });
    }
  }

  /**
   * Model initilization
   *
   * @param serviceTypes Assessment service types edit object.
   */
  private initModel(serviceTypes: any): void
  {
    if (serviceTypes === undefined || this.loaded)
    {
      return;
    }

    // Build form
    this.editForm = this.fb.group({
      serviceTypes: this.fb.array([])
    });

    if (serviceTypes && serviceTypes.length > 0)
    {
      serviceTypes.forEach(data =>
      {
        this.addServiceType(data.id, data.version, data.serviceType, data.score, data.query, false, data.importError);
      });
    }

    this.editForm.valueChanges.subscribe({
      next: () =>
      {
        if (!this.isDeleted && !this.isLocked)
        {
          this.formChanged.emit(this.editForm);
        }
      }
    });

    // Must apply a delay here because isDeleted or isLocked is not set yet when setting the model.
    setTimeout(() =>
    {
      this.disableControls();
      this.loaded = true;
    });
  }

  /**
   * Create a form group.
   */
  private initServiceType(id: number, version: number, serviceType: any, score: number, query: any, showRules: boolean,
    importError: boolean): UntypedFormGroup
  {
    if (query === null)
    {
      query = {
        condition: 'and',
        rules: []
      };
    }
    return this.fb.group({
      guid: Guid.create().toString(),
      id: [id],
      version: [version],
      serviceType: [serviceType, Validators.required],
      score: [score, Validators.required],
      query: [query, Validators.required],
      showRules: [showRules],
      importError: [importError]
    });
  }
}
